import type { DefaultComputedContentSource } from '../types';
import type {
    ComputedContentSourceDependency,
    ComputedContentSourceResolved,
    ContentComputerState,
    ContentComputerSubsetState,
} from './types';
import { getComputeContentId, hash, hashDependencyRef } from './utils';

/**
 * Unfill dependency values from a computed source.
 */
export function unfillComputedContentSource<Source extends DefaultComputedContentSource>(
    source: Source
): Source {
    const result: Source['dependencies'] = {};

    Object.entries(source.dependencies ?? {}).forEach(([dependencyName, dependency]) => {
        result[dependencyName] = {
            ref: dependency.ref,
        };
    });

    return {
        ...source,
        dependencies: result,
    };
}

/**
 * Fill a computed source with the dependency values.
 */
export function fillComputedContentSource<Source extends DefaultComputedContentSource>(
    source: Source,
    state: ContentComputerSubsetState
): ComputedContentSourceResolved<Source> {
    // @ts-ignore
    const result: ComputedContentSourceResolved<Source>['dependencies'] = {};

    Object.entries(source.dependencies ?? {}).forEach(([dependencyName, dependency]) => {
        const dependencyHash = hashDependencyRef(dependency);
        const dependencyState = state.dependencies[dependencyHash];
        if (!dependencyState) {
            throw new Error(`Dependency ${dependencyName} not found in state (${dependencyHash})`);
        }

        // @ts-ignore
        result[dependencyName] = {
            ref: dependencyState.ref,
            value: dependencyState.value,
        };
    });

    return {
        ...source,
        dependencies: result,
    };
}

/**
 * Extract a sub-state of the global state.
 */
export function extractComputedContentSubsetState<Source extends DefaultComputedContentSource>(
    state: ContentComputerState,
    sources: Source[]
): {
    result: ContentComputerSubsetState;
    allDependencies: ComputedContentSourceDependency<Source>[];
    dependenciesToFetch: ComputedContentSourceDependency<Source>['ref'][];
    sourceTypesToFetch: Source['type'][];
} {
    const result: ContentComputerSubsetState = {
        complete: false,
        types: {},
        dependencies: {},
    };

    const allDependencies = new Map<string, ComputedContentSourceDependency<Source>>();
    const dependenciesToFetch = new Map<string, ComputedContentSourceDependency<Source>['ref']>();
    const sourceTypesToFetch = new Set<Source['type']>();

    sources.forEach((source) => {
        (
            Object.entries(source.dependencies ?? {}) as [
                string,
                ComputedContentSourceDependency<Source>,
            ][]
        ).forEach(([_dependencyName, dependency]) => {
            const dependencyHash = hashDependencyRef(dependency);

            allDependencies.set(dependencyHash, dependency);

            // Use provided value if any
            if ('value' in dependency) {
                result.dependencies[dependencyHash] = {
                    versionHash: hash(dependency as ComputedContentSourceDependency<Source>),
                    ref: dependency.ref,
                    value: dependency.value,
                };
                return;
            }

            // Reuse dependencies from the parent state if possible
            if (state.dependencies[dependencyHash]) {
                result.dependencies[dependencyHash] = state.dependencies[dependencyHash];
                return;
            }

            dependenciesToFetch.set(dependencyHash, dependency.ref);
        });

        const parentVersion = state.types[source.type];
        if (typeof parentVersion === 'string') {
            result.types[source.type] = parentVersion;
        } else {
            sourceTypesToFetch.add(source.type);
        }
    });

    result.complete = sourceTypesToFetch.size === 0 && dependenciesToFetch.size === 0;

    return {
        result,
        allDependencies: Array.from(allDependencies.values()),
        dependenciesToFetch: Array.from(dependenciesToFetch.values()),
        sourceTypesToFetch: Array.from(sourceTypesToFetch),
    };
}

/**
 * Get an ID for the result of a source computation.
 */
export function getComputedSourceResultId({
    source,
    seed,
    state,
}: {
    source: DefaultComputedContentSource;
    seed: string;
    state: ContentComputerSubsetState;
}) {
    assertComputedContentSubsetState({ source, state });

    // The ID is a hash of the source, the seed and the state
    // It is used to cache the result and to avoid recomputing the result if the parameters are the same.
    //
    // We only extract ref from the source as the value of each dependency (unfill) is contained in the "state".
    return getComputeContentId(
        `${source.type}_${hash({ source: unfillComputedContentSource(source), seed, state })}`
    );
}

/**
 * Assert that a state is a proper sub-state for a source.
 */
export function assertComputedContentSubsetState({
    source,
    state,
}: {
    source: DefaultComputedContentSource;
    state: ContentComputerSubsetState;
}) {
    // Safety check to ensure that the state is a proper sub-state
    if (Object.keys(state.types).length > 1) {
        throw new Error('The state must contain only one source type');
    } else if (!state.types[source.type]) {
        throw new Error('The state must contain the source type');
    }

    const usedDependencies = new Set<string>();
    Object.values(state.dependencies).forEach((dependencyState) => {
        const hashRef = hashDependencyRef(dependencyState);
        if (!state.dependencies[hashRef]) {
            throw new Error(
                `The state must contain the dependency ref for "${JSON.stringify(
                    dependencyState.ref
                )}"`
            );
        }

        usedDependencies.add(hashRef);
    });

    if (usedDependencies.size !== Object.keys(state.dependencies).length) {
        throw new Error('The state must only contain the dependencies used by the source');
    }
}
